import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import LoadingOverlay from './LoadingOverlay';
import { UserContext } from './UserContext';
import { API_ENDPOINTS } from '../config/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CategoryAnalysis = () => {
    const [filter, setFilter] = useState('overall'); // Options: today, yesterday, week, month, overall
    const [categories, setCategories] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const context = useContext(UserContext);
    const { user, restaurant } = context || {};
    const restaurantId = restaurant?._id || user?.id;


    const fetchCategoryData = async () => {
        if (!restaurantId) return;
        try {
            setIsLoading(true);
            const response = await axios.get(`${API_ENDPOINTS.ANALYTICS_CATEGORY(restaurantId)}?period=${filter}`);
            const data = response.data;
            setCategories(data);

            // Prepare chart data: labels from category names and datasets for quantity + revenue
            const labels = data.map(cat => cat._id);
            const quantityData = data.map(cat => cat.totalQuantity);
            const revenueData = data.map(cat => cat.totalRevenue);
            setChartData({
                labels,
                datasets: [
                    {
                        label: 'Total quantity',
                        data: quantityData,
                        backgroundColor: 'rgba(34, 197, 94, 0.75)', // emerald
                        borderColor: 'rgba(22, 163, 74, 1)',
                        borderWidth: 1,
                        borderRadius: 6,
                        borderSkipped: false,
                        maxBarThickness: 32,
                        yAxisID: 'y',
                    },
                    {
                        label: 'Total revenue (₹)',
                        data: revenueData,
                        backgroundColor: 'rgba(249, 115, 22, 0.75)', // orange
                        borderColor: 'rgba(248, 150, 59, 1)',
                        borderWidth: 1,
                        borderRadius: 6,
                        borderSkipped: false,
                        maxBarThickness: 32,
                        yAxisID: 'y1',
                    },
                ]
            });
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error('Error fetching category analysis data:', error);
        }
    };

    useEffect(() => {
        if (restaurantId) {
            fetchCategoryData();
        }
    }, [filter, restaurantId]);

    return (
        <>
            {isLoading && <LoadingOverlay />}
            <div className="p-2 sm:p-3 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">Period</span>
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="border border-stone-300 rounded-lg px-2.5 py-1.5 text-xs bg-white focus:ring-2 focus:ring-orange-400 outline-none"
                        >
                            <option value="today">Today</option>
                            <option value="yesterday">Yesterday</option>
                            <option value="week">This week</option>
                            <option value="month">This month</option>
                            <option value="overall">Overall</option>
                        </select>
                    </div>
                    <button
                        type="button"
                        onClick={() => fetchCategoryData()}
                        className="px-2.5 py-1.5 text-[11px] font-medium rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50"
                    >
                        ↻ Refresh
                    </button>
                </div>
                {chartData && (
                    <div className="rounded-lg bg-stone-50/50 border border-stone-200 p-2 w-full h-48 sm:h-80">
                        <Bar
                            data={chartData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { position: 'bottom', labels: { boxWidth: 8, font: { size: 10 } } },
                                    title: { display: false },
                                    tooltip: {
                                        callbacks: {
                                            label: (context) => {
                                                const value = context.parsed.y || 0;
                                                const label = context.dataset.label || '';
                                                if (label.toLowerCase().includes('revenue')) {
                                                    return `${label}: ₹${value.toLocaleString('en-IN')}`;
                                                }
                                                return `${label}: ${value.toLocaleString('en-IN')}`;
                                            },
                                        },
                                    },
                                },
                                scales: {
                                    x: {
                                        ticks: { font: { size: 9 }, maxRotation: 45, minRotation: 0 },
                                        grid: { display: false },
                                    },
                                    y: {
                                        type: 'linear',
                                        display: true,
                                        position: 'left',
                                        ticks: { font: { size: 9 } },
                                        title: { display: true, text: 'Quantity' },
                                        grid: { color: 'rgba(148, 163, 184, 0.2)', borderDash: [4, 4], drawBorder: false },
                                    },
                                    y1: {
                                        type: 'linear',
                                        display: true,
                                        position: 'right',
                                        ticks: {
                                            font: { size: 9 },
                                            callback: (value) => `₹${Number(value).toLocaleString('en-IN')}`,
                                        },
                                        title: { display: true, text: 'Revenue (₹)' },
                                        grid: { drawOnChartArea: false },
                                    },
                                },
                            }}
                        />
                    </div>
                )}
                <div className="overflow-x-auto rounded-lg border border-stone-200 w-full">
                    <table className="min-w-full text-xs">
                        <thead>
                            <tr className="bg-stone-50 border-b border-stone-200">
                                <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-500">Category</th>
                                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-stone-500">Total quantity</th>
                                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-stone-500">Total revenue (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map((cat, idx) => (
                                <tr key={idx} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/50">
                                    <td className="px-3 py-2 text-stone-800 font-medium">{cat._id}</td>
                                    <td className="px-3 py-2 text-right text-stone-600">{cat.totalQuantity}</td>
                                    <td className="px-3 py-2 text-right text-stone-600">{cat.totalRevenue.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default CategoryAnalysis;
